import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.9 2.4 30.3 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.9 6.1C12.3 13.2 17.7 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 2.9-2.2 5.3-4.7 7l7.3 5.7c4.3-3.9 6.8-9.8 6.8-17.2z"/>
    <path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.7l7.9-6.1z"/>
    <path fill="#34A853" d="M24 48c6.3 0 11.6-2.1 15.5-5.7l-7.3-5.7c-2 1.4-4.7 2.3-8.2 2.3-6.3 0-11.7-3.7-13.6-9.3l-7.9 6.1C6.4 42.6 14.6 48 24 48z"/>
  </svg>
);

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return "Login failed";
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (location.state?.error) toast.error(location.state.error);
  }, [location.state]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-transparent border-0 border-b border-black/15 rounded-none px-0 py-3 text-[#0E1E2E] placeholder:text-[#8797A3] focus:border-brand transition-colors";

  return (
    <div className="min-h-screen bg-[#F1F4F3] flex flex-col justify-center px-6 md:px-12">
      <Link to="/" className="absolute top-8 left-6 md:left-12 font-mono2 text-xs uppercase tracking-[0.2em] text-[#556673] hover:text-[#0E1E2E] flex items-center gap-2">
        <ArrowLeft size={14} /> Back to site
      </Link>
      <div className="max-w-md w-full mx-auto">
        <div className="mb-8"><Brand size={40} /></div>
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-brand mb-4">Team Access</p>
        <h1 className="font-display font-black uppercase tracking-tight text-4xl md:text-5xl mb-10 text-[#0E1E2E]">Admin Login</h1>
        <form onSubmit={submit} data-testid="login-form" className="flex flex-col gap-8">
          <div>
            <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673] mb-2 block">Email</label>
            <input data-testid="login-email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="hello@umgtmedia.com" />
          </div>
          <div>
            <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673] mb-2 block">Password</label>
            <input data-testid="login-password" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <button data-testid="login-submit" disabled={busy}
            className="px-8 py-4 bg-brand text-[#06111C] font-display font-bold uppercase tracking-wide text-sm hover:bg-[#27B95B] transition-colors disabled:opacity-50">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px bg-black/10" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8797A3]">or</span>
          <div className="flex-1 h-px bg-black/10" />
        </div>
        <button type="button" data-testid="google-login-btn" onClick={googleLogin}
          className="w-full flex items-center justify-center gap-3 px-8 py-4 border border-black/15 text-[#0E1E2E] font-mono2 text-xs uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors">
          <GoogleIcon /> Continue with Google
        </button>
      </div>
    </div>
  );
}
